import { useState } from 'react';
import { Container, Grid } from '@mui/material';
import { fetchMySocials } from '../../lib/api';
import { track } from '../../lib/analytics';
import { uid } from '../../lib/format';
import { useOwnedSection } from '../../lib/useOwnedSection';
import PageHeader from '../../components/PageHeader';
import SectionLoading from '../../components/SectionLoading';
import Button from '../../components/Button';
import AddMetricsModal from './AddMetricsModal';
import AddSocialModal from './AddSocialModal';
import SocialCard from './SocialCard';
import EmptyState from '../../components/EmptyState';

function SocialsPage({ identityActions }) {
  const { items: socials, setItems: setSocials, loading } = useOwnedSection(
    fetchMySocials,
    '/v1/profiles/me/socials',
    (next) => ({ socials: next })
  );
  const [addOpen, setAddOpen] = useState(false);
  const [metricsSocial, setMetricsSocial] = useState(null);

  if (loading) return <SectionLoading />;

  function addSocial(account) {
    track('social_added', { entityType: 'social', label: account.platform });
    setSocials((current) => [{ ...account, id: uid('soc') }, ...current]);
    identityActions.showToast('Social account added');
  }

  function updateSocial(id, patch) {
    track('social_updated', { entityType: 'social', entityId: id });
    setSocials((current) => current.map((social) => (social.id === id ? { ...social, ...patch } : social)));
    identityActions.showToast('Social account updated');
  }

  function deleteSocial(id) {
    track('social_deleted', { entityType: 'social', entityId: id });
    setSocials((current) => current.filter((social) => social.id !== id));
    identityActions.showToast('Social account deleted');
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="Social stats"
        description="Manage the key platforms brands care about"
        action={<Button onClick={() => setAddOpen(true)}>Add social profile</Button>}
      />
      {socials.length === 0 ? (
        <EmptyState
          title="No social profiles yet"
          text="Add at least one platform so brands can find your work and understand your audience. This is optional for publishing."
          action={<Button onClick={() => setAddOpen(true)}>Add your first social profile</Button>}
        />
      ) : (
        <Grid container spacing={2}>
          {socials.map((social) => (
            <Grid size={{ xs: 12, md: 6 }} key={social.id}>
              <SocialCard
                social={social}
                onAddMetrics={() => setMetricsSocial(social)}
                onDelete={() => window.confirm('Delete this social account?') && deleteSocial(social.id)}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {addOpen && (
        <AddSocialModal
          onClose={() => setAddOpen(false)}
          onSubmit={(data) => {
            addSocial(data);
            setAddOpen(false);
          }}
        />
      )}
      {metricsSocial && (
        <AddMetricsModal
          social={metricsSocial}
          onClose={() => setMetricsSocial(null)}
          onSubmit={(patch) => {
            updateSocial(metricsSocial.id, patch);
            setMetricsSocial(null);
          }}
        />
      )}
    </Container>
  );
}

export default SocialsPage;
